/**
 * Free Gift Promotions
 *
 * Automatically adds configured gifts when the cart meets qualifying criteria
 * for each promotion block in sections/free-gift-promotions.liquid.
 * Gifts are add-only — they are never automatically removed.
 *
 * Listens for cart:changed events published via theme.publishCartChanged().
 */
(function() {
  'use strict';

  var DEBOUNCE_MS = 300;
  var debounceTimer = null;

  window.theme = window.theme || {};

  window.theme.publishCartChanged = function(cart, source) {
    document.dispatchEvent(new CustomEvent('cart:changed', {
      detail: {
        cart: cart || null,
        source: source || 'unknown'
      }
    }));
  };

  if (!window.freeGiftConfig) return;

  var promotions = (window.freeGiftConfig.promotions || [])
    .map(function(promotion) {
      return {
        id: promotion.id,
        label: promotion.label || promotion.discountCode || promotion.id,
        discountCode: promotion.discountCode || '',
        giftVariantId: parseInt(promotion.giftVariantId, 10),
        triggerType: promotion.triggerType || 'cart_value',
        minValueCents: promotion.minValueCents || 0,
        qualifyingProductIds: promotion.qualifyingProductIds || []
      };
    })
    .filter(function(promotion) {
      return promotion.giftVariantId;
    });

  if (!promotions.length) return;

  var allGiftVariantIds = promotions.map(function(promotion) {
    return promotion.giftVariantId;
  });

  var syncing = false;

  function debounced(fn) {
    return function() {
      var args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        fn.apply(null, args);
      }, DEBOUNCE_MS);
    };
  }

  function getThresholdInPresentmentCents(promotion) {
    var rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return Math.round(promotion.minValueCents * rate);
  }

  function discountIsActive(cart, code) {
    if (!code) return true;

    var codeLower = code.toLowerCase().trim();
    if (!codeLower) return true;

    var i;

    if (cart.discount_codes && cart.discount_codes.length) {
      for (i = 0; i < cart.discount_codes.length; i++) {
        var entry = cart.discount_codes[i];
        var discountCode = (entry.code || entry).toString().toLowerCase();

        if (discountCode === codeLower) {
          return true;
        }
      }
    }

    var titles = [];

    (cart.cart_level_discount_applications || []).forEach(function(d) {
      if (d.title) titles.push(d.title.toLowerCase());
    });

    (cart.items || []).forEach(function(item) {
      (item.line_level_discount_allocations || []).forEach(function(allocation) {
        var application = allocation.discount_application;
        if (application && application.title) {
          titles.push(application.title.toLowerCase());
        }
      });
    });

    for (i = 0; i < titles.length; i++) {
      if (titles[i] === codeLower) return true;
    }

    return false;
  }

  function cartHasGift(cart, giftVariantId) {
    var i;
    for (i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === giftVariantId) return true;
    }
    return false;
  }

  function getSubtotalExcludingGifts(cart) {
    var subtotal = 0;
    var i;

    for (i = 0; i < cart.items.length; i++) {
      if (allGiftVariantIds.indexOf(cart.items[i].variant_id) === -1) {
        subtotal += cart.items[i].final_line_price;
      }
    }

    return subtotal;
  }

  function promotionQualifies(cart, promotion) {
    if (!cart || !cart.items || !cart.items.length) return false;
    if (cartHasGift(cart, promotion.giftVariantId)) return false;

    var subtotalExcludingGifts = getSubtotalExcludingGifts(cart);
    var thresholdCents = getThresholdInPresentmentCents(promotion);
    var valueOk = true;
    var productOk = true;
    var i;

    if (promotion.triggerType === 'cart_value' || promotion.triggerType === 'both') {
      valueOk = subtotalExcludingGifts >= thresholdCents;
    }

    if (promotion.triggerType === 'qualifying_product' || promotion.triggerType === 'both') {
      productOk = false;
      var ids = promotion.qualifyingProductIds || [];
      for (i = 0; i < cart.items.length; i++) {
        if (ids.indexOf(cart.items[i].product_id) !== -1) {
          productOk = true;
          break;
        }
      }
    }

    return valueOk && productOk && discountIsActive(cart, promotion.discountCode);
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(res) {
        if (!res.ok) throw new Error('Could not fetch cart');
        return res.json();
      });
  }

  function addGiftToCart(promotion) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: promotion.giftVariantId,
          quantity: 1,
          properties: {
            _Type: 'FreeGift',
            _PromotionId: promotion.id
          }
        }]
      })
    }).then(function(res) {
      return res.json().then(function(json) {
        if (!res.ok) {
          throw new Error(json.description || 'Cart add failed');
        }
        return json;
      });
    });
  }

  function updateHeaderCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function(el) {
      el.textContent = count;
    });
    var bubble = document.querySelector('[data-cart-count-bubble]');
    if (bubble) bubble.classList.toggle('hide', count === 0);
  }

  function refreshCartUI() {
    if (window.theme.CartDrawer && typeof window.theme.CartDrawer.refresh === 'function') {
      return window.theme.CartDrawer.refresh();
    }
    return fetchCart().then(function(cart) {
      updateHeaderCartCount(cart.item_count);
      return cart;
    });
  }

  function syncPromotion(cart, promotion) {
    if (!promotionQualifies(cart, promotion)) {
      return Promise.resolve(cart);
    }

    return addGiftToCart(promotion).then(function() {
      return fetchCart();
    });
  }

  function syncGift() {
    if (syncing) return Promise.resolve();
    syncing = true;

    var addedGift = false;

    return fetchCart()
      .then(function(cart) {
        var chain = Promise.resolve(cart);

        promotions.forEach(function(promotion) {
          chain = chain.then(function(currentCart) {
            return syncPromotion(currentCart, promotion).then(function(nextCart) {
              if (nextCart !== currentCart) {
                addedGift = true;
              }
              return nextCart;
            });
          });
        });

        return chain;
      })
      .then(function(cart) {
        if (!addedGift) return cart;
        return refreshCartUI();
      })
      .then(function(cart) {
        if (addedGift && cart) {
          window.theme.publishCartChanged(cart, 'free-gift');
        }
        return cart;
      })
      .catch(function(err) {
        if (window.Shopify && window.Shopify.designMode) {
          console.warn('[Free Gift]', err.message || err);
        }
      })
      .finally(function() {
        syncing = false;
      });
  }

  var debouncedSyncGift = debounced(syncGift);

  document.addEventListener('cart:changed', function(evt) {
    var detail = evt.detail || {};
    if (detail.source === 'free-gift') return;

    if (detail.source === 'product-form' || detail.source === 'cart-drawer') {
      syncGift();
      return;
    }

    debouncedSyncGift();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGift);
  } else {
    syncGift();
  }
})();
